/**
 * React Query hooks for category preference management (WP-CAT-11)
 *
 * Provides hooks for:
 * - Fetching categories with preferences
 * - Updating individual category preferences (with optimistic updates)
 * - Bulk updating preferences
 * - Resetting preferences to defaults
 *
 * Pattern: Follows useAccounts.ts query key factory pattern
 * Schema source: services/core/app/schemas/category.py
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesService } from '@/services/categories';
import type { CategoryWithPreference, BulkPreferenceUpdateItem } from '@/types/category';

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query keys for category preferences.
 *
 * Key structure:
 * - ['categories', 'preferences'] - all preference queries
 * - ['categories', 'preferences', { includeDisabled: true }] - list with filters
 *
 * Note: Uses ['categories', 'preferences'] as child of ['categories']
 * so invalidating ['categories'] also invalidates preference queries.
 */
export const categoryPreferenceKeys = {
  all: ['categories', 'preferences'] as const,
  list: (includeDisabled: boolean) =>
    [...categoryPreferenceKeys.all, { includeDisabled }] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch categories with tenant-specific preference data.
 *
 * @param includeDisabled - If true (default), returns all categories.
 *                          If false, returns only enabled categories.
 * @returns Query result with categories including isEnabled field
 *
 * API: GET /categories/with-preferences?include_disabled={bool}
 */
export function useCategoryPreferences(includeDisabled: boolean = true) {
  return useQuery({
    queryKey: categoryPreferenceKeys.list(includeDisabled),
    queryFn: () => categoriesService.getWithPreferences(includeDisabled),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update a single category's preference with optimistic update.
 *
 * - Immediately updates local cache for responsive UI
 * - Rolls back on error
 * - Invalidates both preference and base category queries on settle
 *
 * API: PUT /categories/{id}/preference
 */
export function useUpdateCategoryPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, isEnabled }: { categoryId: string; isEnabled: boolean }) =>
      categoriesService.updatePreference(categoryId, isEnabled),

    // Optimistic update for immediate UI feedback
    onMutate: async ({ categoryId, isEnabled }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: categoryPreferenceKeys.all });

      // Snapshot the previous value for rollback
      const previousCategories = queryClient.getQueryData<CategoryWithPreference[]>(
        categoryPreferenceKeys.list(true)
      );

      // Optimistically update the cache
      queryClient.setQueryData<CategoryWithPreference[] | undefined>(
        categoryPreferenceKeys.list(true),
        (old) =>
          old?.map((cat) =>
            cat.id === categoryId ? { ...cat, isEnabled } : cat
          )
      );

      // Return context for rollback
      return { previousCategories };
    },

    // Rollback on error
    onError: (_err, _variables, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(
          categoryPreferenceKeys.list(true),
          context.previousCategories
        );
      }
    },

    // Invalidate queries after mutation settles (success or error)
    onSettled: () => {
      // Invalidate preference queries
      queryClient.invalidateQueries({ queryKey: categoryPreferenceKeys.all });
      // CRITICAL: Also invalidate base ['categories'] to refresh dropdowns elsewhere
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

/**
 * Bulk update multiple category preferences.
 *
 * Useful for batch operations like "enable all" or debounced rapid toggles.
 *
 * API: POST /categories/preferences/bulk
 */
export function useBulkUpdateCategoryPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: BulkPreferenceUpdateItem[]) =>
      categoriesService.bulkUpdatePreferences(updates),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryPreferenceKeys.all });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

/**
 * Reset category preferences to starter defaults.
 *
 * Clears all custom preferences and re-seeds with ~26 starter categories.
 *
 * API: POST /categories/preferences/reset
 */
export function useResetCategoryPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => categoriesService.resetPreferences(),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryPreferenceKeys.all });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}
